import React from 'react';

// Ported from tqos-admin's shop/Price.tsx. Renders an integer-cents
// value as "$12³⁴" — dollars and cents in adjacent spans, cents styled
// smaller + top-aligned (see `.price/.dollars/.cents` in src/index.css).
//
// web-admin doesn't have an IItem type, so the `variation` prop from the
// original was dropped — callers pass the raw cents value directly.

type Props = {
  /** Price in integer cents (e.g. 1234 = $12.34). */
  price?: number | null;
  /** Render "$0.00" instead of "$X.XX" when price is zero/undefined. */
  showZero?: boolean;
  /** Render nothing at all on zero/undefined. Takes precedence over `showZero`. */
  showZeroAsBlank?: boolean;
  /** Optional id on a hidden mirror div — exposes a flat "$X.XX" string
   *  for screen readers / copy-paste without affecting visual layout. */
  id?: string;
};

const Price: React.FC<Props> = ({ price, showZero, showZeroAsBlank, id }) => {
  const thePrice = price && price > 0 ? price : 0;
  // The original truncates a stringified value at the decimal point
  // before taking cents — same effect, preserved here so off-by-rounding
  // behavior matches whatever the upstream callers expected.
  let priceString = `${thePrice}`;
  const decimalIndex = priceString.indexOf('.');
  if (decimalIndex > 0) {
    priceString = priceString.substring(0, decimalIndex);
  }
  const dollars = Math.trunc(thePrice / 100);
  const cents = +priceString.slice(-2);
  const centsString = cents < 10 ? `0${cents}` : `${cents}`;

  if (dollars + cents <= 0 && showZeroAsBlank) {
    return null;
  }

  if (dollars + cents <= 0 && !showZero) {
    return (
      <>
        $<span className="price"><span className="dollars">X</span><span className="cents">XX</span></span>
      </>
    );
  }

  if (id) {
    return (
      <>
        <div id={id} className="invisible">${dollars}.{centsString}</div>
        $<span className="price"><span className="dollars">{dollars}</span><span className="cents">{centsString}</span></span>
      </>
    );
  }

  return (
    <>
      $<span className="price"><span className="dollars">{dollars}</span><span className="cents">{centsString}</span></span>
    </>
  );
};

export default Price;
